/**
 * @Module: App Component
 * @Status: Ready
 * @Summary: Main application component with routing and Firebase auth
 * @Description: Wraps the app in AuthProvider for Firebase authentication.
 *   Public routes (browse, cook, import) remain accessible without sign-in.
 *   Protected routes (preferences) redirect to /login when unauthenticated.
 * @NextSteps: Add error boundaries and loading states
 */

import React from 'react';
import { Box, Container, Spinner, Center } from '@chakra-ui/react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { KitchenProvider } from './contexts/KitchenContext';
import { PreferencesProvider } from './contexts/PreferencesContext';
import { PriceProvider } from './contexts/PriceContext';
import { ShoppingListProvider } from './contexts/ShoppingListContext';
import RequireAuth from './utils/authGuard';

import Navigation from './components/common/Navigation';
import WelcomeOverlay from './components/common/WelcomeOverlay';
import ErrorBoundary from './components/common/ErrorBoundary';

// Lazy load route components for better performance
const LandingPage = React.lazy(() => import('./components/common/LandingPage'));
const AuthScreen = React.lazy(() => import('./components/auth/AuthScreen'));
const UserPreferences = React.lazy(() => import('./components/user/UserPreferences'));

// v2 recipe screens
const BrowseScreen = React.lazy(() => import('./components/recipe/BrowseScreen'));
const RecipeDetailScreen = React.lazy(() => import('./components/recipe/RecipeDetailScreen'));
const CookScreen = React.lazy(() => import('./components/cooking/CookScreen'));
const ImportScreen = React.lazy(() => import('./components/import/ImportScreen'));
const GenerateScreen = React.lazy(() => import('./components/generate/GenerateScreen'));
const MealPlanScreen = React.lazy(() => import('./components/mealplan/MealPlanScreen'));
const KitchenSetup = React.lazy(() => import('./components/kitchen/KitchenSetup'));
const SharedRecipeScreen = React.lazy(() => import('./components/recipe/SharedRecipeScreen'));
const IngredientRegistryScreen = React.lazy(() => import('./components/price/IngredientRegistryScreen'));
const ProgressScreen = React.lazy(() => import('./components/progress/ProgressScreen'));
const PantryPage = React.lazy(() => import('./components/kitchen/PantryPage'));
const ShoppingListPage = React.lazy(() => import('./components/kitchen/ShoppingListPage'));
const FoodLogScreen = React.lazy(() => import('./components/foodlog/FoodLogScreen'));

/** Route-aware shell — hides nav + container on landing page for clean full-page experience. */
const AppRoutes: React.FC = () => {
	const { user } = useAuth();
	const location = useLocation();
	const isLanding = location.pathname === '/' && localStorage.getItem('choppy:hasSeenWelcome') !== 'true';

	return (
		<Box minH="100vh" bg="surface.page">
			{!isLanding && <Navigation />}
			<WelcomeOverlay />
			{isLanding ? (
				<ErrorBoundary>
				<React.Suspense fallback={<Center h="40vh"><Spinner size="lg" /></Center>}>
					<Routes>
						<Route path="/" element={<LandingPage />} />
					</Routes>
				</React.Suspense>
				</ErrorBoundary>
			) : (
				<Container maxW="container.xl" py={8}>
					<ErrorBoundary>
					<React.Suspense fallback={<Center h="40vh"><Spinner size="lg" /></Center>}>
						<Routes>
							{/* Auth route */}
							<Route
								path="/login"
								element={!user ? <AuthScreen /> : <Navigate to="/browse" />}
							/>

							{/* Public routes — no auth required */}
							<Route path="/browse" element={<BrowseScreen />} />
							<Route path="/recipe/:recipeId" element={<RecipeDetailScreen />} />
							<Route path="/cook/:recipeId" element={<CookScreen />} />
							<Route path="/import" element={<ImportScreen />} />
							<Route path="/generate" element={<GenerateScreen />} />
							<Route path="/meal-plan" element={<MealPlanScreen />} />
							<Route path="/kitchen" element={<KitchenSetup />} />
							<Route path="/shared" element={<SharedRecipeScreen />} />
							<Route path="/pantry" element={<PantryPage />} />
							<Route path="/shopping" element={<ShoppingListPage />} />
							<Route path="/prices" element={<IngredientRegistryScreen />} />
							<Route path="/log" element={<FoodLogScreen />} />
							<Route path="/progress" element={<ProgressScreen />} />
							<Route path="/recipes" element={<Navigate to="/browse" />} />

							{/* Protected routes */}
							<Route
								path="/preferences"
								element={
									<RequireAuth>
										<UserPreferences />
									</RequireAuth>
								}
							/>

							{/* Landing page for first-time visitors */}
							<Route path="/" element={
								localStorage.getItem('choppy:hasSeenWelcome') === 'true'
									? <Navigate to="/browse" />
									: <LandingPage />
							} />
							{/* Fallback */}
							<Route path="*" element={<Navigate to="/browse" />} />
						</Routes>
					</React.Suspense>
					</ErrorBoundary>
				</Container>
			)}
		</Box>
	);
};

/** Inner shell — needs to live inside AuthProvider so useAuth() works. */
const AppShell: React.FC = () => {
	const { loading } = useAuth();

	if (loading) {
		return (
			<Center h="100vh" bg="surface.page">
				<Spinner size="xl" color="text.secondary" />
			</Center>
		);
	}

	return (
		<Router>
			<AppRoutes />
		</Router>
	);
};

export const App: React.FC = () => (
	<AuthProvider>
		<KitchenProvider>
			<PreferencesProvider>
				<PriceProvider>
					<ShoppingListProvider>
						<AppShell />
					</ShoppingListProvider>
				</PriceProvider>
			</PreferencesProvider>
		</KitchenProvider>
	</AuthProvider>
);

export default App;
